import type {WixImageInfo} from '@wix/devzai-common-wix';
import type {Values} from '@wix/devzai-utils-common';

/**
 * Registration type for events.
 * Indicates whether the event is a ticketed event or an RSVP event.
 */
export const DoppeDtoEventRegistrationType = {
    /**
     * Registration type is unknown or not available.
     */
    Unknown: 'Unknown',
    /**
     * Event requires RSVP registration.
     */
    RSVP: 'RSVP',
    /**
     * Event requires ticket purchase for registration.
     */
    Ticketing: 'Ticketing',
} as const;

export type DoppeDtoEventRegistrationType = Values<typeof DoppeDtoEventRegistrationType>;

export interface DoppeDtoEvent {
    id: string;
    name: string;
    description?: string;
    media: WixImageInfo[] | null;
    eventDates: {
        eventStartDate: string | null;
        eventEndDate: string | null;
        formattedEventDates: string;
        /** Whether the time zone should be displayed with the date. Retrieved from Wix Events API. */
        showTimeZone: boolean;
        /** Event time zone ID in TZ database format (e.g., "America/New_York"). Retrieved from Wix Events API. */
        timeZoneId: string | null;
    };
    eventPageUrl?: string;
    createdDate: Date | null;
    highestPrice: string | null;
    lowestPrice: string | null;
    location: string | null;
    slug: string;
    /**
     * The registration type of the event (RSVP or Ticketing).
     * Determines whether the event shows "Buy Tickets" or "RSVP" button.
     */
    registrationType: DoppeDtoEventRegistrationType;
    /** Badge text to display as a ribbon on the event card. Resolved from the highest-priority Wix Events badge. */
    ribbon?: string;
}

export namespace DoppeDtoEvent {
    export type Id = DoppeDtoEvent['id'];
}

/**
 * Options for creating a dummy event with localized content and images.
 */
export interface DoppeDtoEventCreateDummyOptions {
    /**
     * Localized event name.
     */
    name: string;
    /**
     * Localized event description.
     */
    description: string;
    /**
     * Localized "Date TBD" text.
     */
    dateTBD: string;
    /**
     * Event image (WixImageInfo format).
     */
    image: WixImageInfo | null;
}

/**
 * Creates a dummy event with placeholder content.
 * Used when no actual event is available (e.g., for preview or empty state).
 */
export function doppeDtoEventCreateDummy(
    options: DoppeDtoEventCreateDummyOptions
): DoppeDtoEvent {
    return {
        id: 'dummy-event',
        name: options.name,
        description: options.description,
        media: options.image ? [options.image] : null,
        eventDates: {
            eventStartDate: null,
            eventEndDate: null,
            formattedEventDates: options.dateTBD,
            showTimeZone: false,
            timeZoneId: null,
        },
        createdDate: null,
        highestPrice: null,
        lowestPrice: null,
        location: null,
        slug: 'dummy-event',
        registrationType: DoppeDtoEventRegistrationType.Ticketing,
    };
}

/**
 * Formats event dates using the event's own timezone (from `timeZoneId`),
 * producing a display string like "25 February 2026, 19:00–23:00 MST".
 * Falls back to the API-provided `formattedEventDates` when the start date is unavailable (TBD).
 */
export function doppeDtoEventFormatDates(
    eventDates: DoppeDtoEvent['eventDates']
): string {
    const {eventStartDate, eventEndDate, timeZoneId, showTimeZone, formattedEventDates} = eventDates;

    if (!eventStartDate) {
        return formattedEventDates;
    }

    const tzOptions: Intl.DateTimeFormatOptions = timeZoneId ? {timeZone: timeZoneId} : {};

    const startDate = new Date(eventStartDate);

    const dateFormatter = new Intl.DateTimeFormat('en', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        ...tzOptions,
    });

    const timeFormatter = new Intl.DateTimeFormat('en', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        ...tzOptions,
    });

    const dateStr = dateFormatter.format(startDate);
    const startTimeStr = timeFormatter.format(startDate);

    let tzSuffix = '';
    if (showTimeZone && timeZoneId) {
        const parts = new Intl.DateTimeFormat('en', {
            timeZoneName: 'short',
            ...tzOptions,
        }).formatToParts(startDate);
        const tzPart = parts.find(part => part.type === 'timeZoneName');
        if (tzPart) {
            tzSuffix = ` ${tzPart.value}`;
        }
    }

    if (eventEndDate) {
        const endDate = new Date(eventEndDate);
        const endTimeStr = timeFormatter.format(endDate);
        const endDateStr = dateFormatter.format(endDate);

        if (dateStr === endDateStr) {
            return `${dateStr}, ${startTimeStr}–${endTimeStr}${tzSuffix}`;
        } else {
            return `${dateStr}, ${startTimeStr} – ${endDateStr}, ${endTimeStr}${tzSuffix}`;
        }
    }

    return `${dateStr}, ${startTimeStr}${tzSuffix}`;
}
